// src/utils/registreNcs.ts — SCE Issues
// Retrouver le N° d'une NC-S (« NC-S_2026-97 ») dans le registre de la
// qualité, à partir de la déclaration envoyée depuis l'appli (06/10).
//
// ─── Le registre ───
//
// « NC, DP, Events & Actions.xlsm » (site QM_SCE), onglet « NonConformity »,
// tableau Excel `Tableau7` : une ligne par NC, ~400 NC-S. La qualité y
// RETAPE chaque déclaration reçue : priorité en Low/Medium/High, « Notified
// by » écrit de plusieurs façons pour une même personne (« Vautelin
// Sébastien », « Vautelin sebastien », « Rusé Romuald »), description
// reformulée. Le rapprochement est donc tolérant (casse, accents, ordre des
// mots), et vérifié par plusieurs indices.
//
// ─── La règle du numéro croissant (Guillaume, 06/10) ───
//
// La qualité numérote dans l'ordre d'enregistrement. Notre NC-S, envoyée le
// jour J, porte donc forcément un numéro PLUS GRAND que tous ceux déjà
// enregistrés avant J la même année : on écarte d'office toutes les lignes
// en dessous. Une NC-S ancienne sur le même NVS, par la même personne, ne
// peut plus être prise pour la nôtre. Un numéro déjà porté par un autre
// problème de l'appli est écarté aussi.
//
// ─── Écrire seul, ou demander ───
//
// Écrit d'office : la ligne qui cite l'ID du problème dans sa description,
// ou une seule ligne nettement en tête (2 points d'avance sur la suivante)
// avec, au choix :
//   - le même NVS ET (la même personne ou la même date de notification) ;
//   - la même personne, la même date de notification ET la désignation
//     (« 3 sur 3 », 06/10 : la qualité retape souvent le composant sans
//     le NVS qu'on lui a donné).
// Sinon les lignes possibles sont proposées : le badge demande de confirmer.
//
// Pur : aucun React, aucun réseau.

import { chargerJSZip } from '@/utils/xlsxCellules'
import { feuillesDuClasseur, jourExcel, lire, lireCellules, lireTableau, textesPartages, type ValeurLue } from '@/utils/xlsxLecture'

// ─── Le registre ───

export const ONGLET_REGISTRE = 'NonConformity'

/** Colonnes lues dans le tableau du registre (noms des en-têtes, retours à la ligne en espaces). */
export const COLONNES_REGISTRE = {
  numero: 'NC #',
  type: 'NC type',
  enregistreLe: 'Recording date (in the tool)',
  notifieLe: 'Notification Date to SCE',
  notifiePar: 'Notified by',
  fournisseur: 'Customer / Supplier Name',
  designation: 'Product designation',
  references: 'Part(s) number(s)',
  serie: 'Serial Number',
  description: 'NonConformity description (is not / expectation)',
} as const

/**
 * Colonnes de SUIVI (06/10, badge de la fiche) : lues si elles existent — un
 * renommage par la qualité n'empêche pas de retrouver le N°.
 */
export const COLONNES_SUIVI = {
  statut: 'NC Status',
  pilote: 'NC Leader',
  pays: 'Customer / Supplier Country',
  decision: 'NC Legitimacy',
  correction: 'Type of correction',
  avancement: 'Containment & Correction Status',
  rca: 'RCA Status (or 8D status for NC-S)',
  clotureLe: 'NC Report Closure Date',
  quantite: 'Quantity NC',
} as const

export interface LigneRegistre {
  /** `NC-S_2026-97` */
  numero: string
  annee: number
  rang: number
  /** Jours `AAAA-MM-JJ` ; null si la cellule n'est pas une date. */
  enregistreLe: string | null
  notifieLe: string | null
  notifiePar: string
  fournisseur: string
  designation: string
  /** NVS cités dans « Part(s) number(s) » : `['NVS39496', 'NVS39494']`. */
  nvs: string[]
  serie: string
  /** ID de problèmes de l'appli cités dans la description (`P2026-0042`). */
  problemes: string[]
  // ─── Suivi par la qualité (facultatif : colonnes peut-être absentes) ───
  statut?: string
  pilote?: string
  pays?: string
  decision?: string
  correction?: string
  avancement?: string
  rca?: string
  clotureLe?: string | null
  quantite?: string
}

const MOTIF_NUMERO = /^NC-S_(\d{4})-([1-9]\d{0,3})$/

/** NVS d'un texte, normalisés : « nvs 39496 + NVS39494 » → `['NVS39496', 'NVS39494']`. */
export function nvsDe(texte: string): string[] {
  return [...new Set([...texte.matchAll(/NVS\s*-?\s*(\d{3,})/gi)].map((m) => `NVS${m[1]}`))]
}

/** ID de problèmes cités dans un texte. */
export function problemesCites(texte: string): string[] {
  return [...new Set([...texte.matchAll(/\bP(20\d{2})-(\d{4})\b/gi)].map((m) => `P${m[1]}-${m[2]}`))]
}

const texte = (v: ValeurLue | undefined): string => (v === undefined ? '' : String(v)).trim()

/** Les NC-S du registre (réclamations fournisseur numérotées), dans l'ordre du tableau. */
export async function lireRegistreNcs(octets: ArrayBuffer | Uint8Array): Promise<LigneRegistre[]> {
  const zip = await (await chargerJSZip()).loadAsync(octets)
  const lignes = await lireTableau(zip, ONGLET_REGISTRE, Object.values(COLONNES_REGISTRE), Object.values(COLONNES_SUIVI))
  const c = COLONNES_REGISTRE
  const s = COLONNES_SUIVI
  return lignes.flatMap((l): LigneRegistre[] => {
    const m = MOTIF_NUMERO.exec(texte(l[c.numero]))
    if (!m || texte(l[c.type]) !== 'Complaint_supplier') return []
    return [{
      numero: m[0],
      annee: Number(m[1]),
      rang: Number(m[2]),
      enregistreLe: jourExcel(l[c.enregistreLe]),
      notifieLe: jourExcel(l[c.notifieLe]),
      notifiePar: texte(l[c.notifiePar]),
      fournisseur: texte(l[c.fournisseur]),
      designation: texte(l[c.designation]),
      nvs: nvsDe(texte(l[c.references])),
      serie: texte(l[c.serie]),
      problemes: problemesCites(texte(l[c.description])),
      statut: texte(l[s.statut]),
      pilote: texte(l[s.pilote]),
      pays: texte(l[s.pays]),
      decision: texte(l[s.decision]),
      correction: texte(l[s.correction]),
      avancement: texte(l[s.avancement]),
      rca: texte(l[s.rca]),
      clotureLe: jourExcel(l[s.clotureLe]),
      quantite: texte(l[s.quantite]),
    }]
  })
}

// ─── La déclaration envoyée ───

export interface DeclarationEnvoyee {
  issueId: string
  /** Jour d'envoi (événement « NC-S envoyée à la qualité »), `AAAA-MM-JJ`. */
  envoyeeLe: string
  /** Date de notification du formulaire (B7). */
  notifieLe: string | null
  initiateur: string
  designation: string
  nvs: string[]
  /** N° de série de la pièce. */
  serie: string
}

/** Relit le formulaire déposé (onglet « NCDeclaration ») : la source de vérité. */
export async function lireDeclarationEnvoyee(
  octets: ArrayBuffer | Uint8Array,
  issueId: string,
  envoyeeLe: string,
): Promise<DeclarationEnvoyee> {
  const zip = await (await chargerJSZip()).loadAsync(octets)
  const feuille = (await feuillesDuClasseur(zip)).find((f) => f.nom === 'NCDeclaration')
  if (!feuille?.chemin) throw new Error('Formulaire NC-S illisible : onglet « NCDeclaration » introuvable')
  const lignes = lireCellules(await lire(zip, feuille.chemin), await textesPartages(zip), 7, 20)
  const cellule = (ligne: number, colonne: number): ValeurLue | undefined => lignes.get(ligne)?.get(colonne)
  return {
    issueId,
    envoyeeLe,
    notifieLe: jourExcel(cellule(7, 2)),
    initiateur: texte(cellule(10, 2)),
    designation: texte(cellule(18, 2)),
    nvs: nvsDe(texte(cellule(18, 5))),
    serie: texte(cellule(19, 5)),
  }
}

/**
 * Repli si le fichier ne se lit pas : son nom (« NC-S <Composant> <NVS>
 * JJMMAA ») et l'auteur de l'événement.
 */
export function declarationDepuisNom(fichier: string, issueId: string, envoyeeLe: string, initiateur: string): DeclarationEnvoyee {
  const nom = fichier.replace(/\.xlsx$/i, '').replace(/\s*\(\d+\)$/, '')
  const date = /\s(\d{2})(\d{2})(\d{2})$/.exec(nom)
  const nvs = nvsDe(nom)
  const designation = nom
    .replace(/^NC-S\s+/i, '')
    .replace(/\s\d{6}$/, '')
    .replace(/NVS\s*-?\s*\d{3,}/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  return {
    issueId,
    envoyeeLe,
    notifieLe: date ? `20${date[3]}-${date[2]}-${date[1]}` : null,
    initiateur,
    designation,
    nvs,
    serie: '',
  }
}

// ─── Le rapprochement ───

/** Sans accents, en minuscules. */
function simplifier(t: string): string {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** « Vautelin  Sébastien » = « sebastien vautelin » = « VAUTELIN Sebastien ». */
export function memePersonne(a: string, b: string): boolean {
  const cle = (t: string): string => simplifier(t).split(/[^a-z]+/).filter(Boolean).sort().join(' ')
  return cle(a) !== '' && cle(a) === cle(b)
}

/** Part des mots (3 lettres et plus) de la plus courte désignation présents dans l'autre. */
function recouvrement(a: string, b: string): number {
  const mots = (t: string): Set<string> => new Set(simplifier(t).split(/[^a-z0-9]+/).filter((m) => m.length >= 3))
  const ma = mots(a)
  const mb = mots(b)
  const petit = ma.size <= mb.size ? ma : mb
  const grand = petit === ma ? mb : ma
  if (petit.size === 0) return 0
  return [...petit].filter((m) => grand.has(m)).length / petit.size
}

function ecartJours(a: string, b: string): number {
  return Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000
}

export interface Candidat {
  ligne: LigneRegistre
  score: number
  /** Ce qui concorde, pour l'affichage : « NVS39496 », « Vautelin sebastien »… */
  raisons: string[]
}

export type ResultatRecherche =
  | { etat: 'trouvee'; candidat: Candidat; certaine: boolean }
  | { etat: 'a-choisir'; candidats: Candidat[] }
  | { etat: 'absente' }

/**
 * Plus haut rang enregistré AVANT le jour d'envoi, par année : notre NC-S
 * est forcément au-dessus.
 */
export function seuilsParAnnee(registre: readonly LigneRegistre[], envoyeeLe: string): Map<number, number> {
  const seuils = new Map<number, number>()
  for (const l of registre) {
    if (l.enregistreLe && l.enregistreLe < envoyeeLe) seuils.set(l.annee, Math.max(seuils.get(l.annee) ?? 0, l.rang))
  }
  return seuils
}

/**
 * Cherche la ligne du registre qui correspond à la déclaration.
 * @param prises numéros déjà portés par d'autres problèmes de l'appli.
 */
export function chercherDansRegistre(
  declaration: DeclarationEnvoyee,
  registre: readonly LigneRegistre[],
  prises: ReadonlySet<string>,
): ResultatRecherche {
  const annee = Number(declaration.envoyeeLe.slice(0, 4))
  const seuils = seuilsParAnnee(registre, declaration.envoyeeLe)
  const eligibles = registre.filter(
    (l) => l.annee >= annee && l.rang > (seuils.get(l.annee) ?? 0) && !prises.has(l.numero),
  )

  // Notre ID recopié dans la description : c'est elle.
  const citant = eligibles.filter((l) => l.problemes.includes(declaration.issueId))
  if (citant.length === 1 && citant[0]) {
    return { etat: 'trouvee', certaine: true, candidat: { ligne: citant[0], score: 100, raisons: [`cite ${declaration.issueId}`] } }
  }

  const candidats = eligibles
    .map((ligne): Candidat & { nvs: boolean; personne: boolean; dateExacte: boolean; designation: boolean } => {
      const raisons: string[] = []
      let score = 0
      const nvsCommuns = declaration.nvs.filter((n) => ligne.nvs.includes(n))
      const nvs = nvsCommuns.length > 0
      if (nvs) { score += 3; raisons.push(nvsCommuns.join(', ')) }
      const personne = memePersonne(declaration.initiateur, ligne.notifiePar)
      if (personne) { score += 2; raisons.push(ligne.notifiePar) }
      let dateExacte = false
      if (declaration.notifieLe && ligne.notifieLe) {
        const ecart = ecartJours(declaration.notifieLe, ligne.notifieLe)
        if (ecart === 0) { score += 2; dateExacte = true; raisons.push('même date de notification') }
        else if (ecart <= 3) { score += 1; raisons.push('date de notification proche') }
      }
      const designation = recouvrement(declaration.designation, ligne.designation) >= 0.5
      if (designation) { score += 1; raisons.push('désignation') }
      const serie = simplifier(declaration.serie).replace(/[^a-z0-9]/g, '')
      if (serie.length >= 3 && simplifier(ligne.serie).replace(/[^a-z0-9]/g, '').includes(serie)) {
        score += 1
        raisons.push('N° de série')
      }
      return { ligne, score, raisons, nvs, personne, dateExacte, designation }
    })
    .filter((c) => c.score >= 3)
    .sort((a, b) => b.score - a.score || a.ligne.rang - b.ligne.rang)

  const [premier, second] = candidats
  const indicesSuffisants = (c: NonNullable<typeof premier>): boolean =>
    (c.nvs && (c.personne || c.dateExacte)) || (c.personne && c.dateExacte && c.designation)
  if (
    premier &&
    indicesSuffisants(premier) &&
    premier.score >= 5 &&
    (!second || second.score <= premier.score - 2)
  ) {
    return { etat: 'trouvee', certaine: false, candidat: { ligne: premier.ligne, score: premier.score, raisons: premier.raisons } }
  }
  if (candidats.length > 0) {
    return {
      etat: 'a-choisir',
      candidats: candidats.slice(0, 3).map(({ ligne, score, raisons }) => ({ ligne, score, raisons })),
    }
  }
  return { etat: 'absente' }
}
